/**
 * Display helpers shared by the service screens: darba vienība (1 DV = 10 min) as "5 DV · 50 min",
 * NET money with two decimals as the design prints it ("35.00"), and the group colours of the status
 * tags (identical in every module — design svc.css `.g-*`).
 */

import type { WorkOrderGroup } from '../types/serviceSetupTypes';
import type { TFunction } from 'i18next';

export const DV_MINUTES = 10;

/** Minutes as "50 min", "1 h", "1 h 30 min". */
export function formatMinutes(minutes: number, t: TFunction): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return t('service:units.min', { count: m });
  if (m === 0) return t('service:units.h', { count: h });
  return `${t('service:units.h', { count: h })} ${t('service:units.min', { count: m })}`;
}

/** "5 DV · 50 min". */
export function formatNorm(normDv: number, t: TFunction): string {
  return `${t('service:units.dv', { count: normDv })} · ${formatMinutes(normDv * DV_MINUTES, t)}`;
}

/** A NET amount with two decimals ("26.00"), or an em dash. */
export function formatNet(amount: number | null | undefined): string {
  return amount === null || amount === undefined ? '—' : amount.toFixed(2);
}

/** Parse a user-typed amount ("26", "26,5", "26.50"); null for empty, NaN for garbage. */
export function parseAmount(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '') return null;
  return /^\d+(\.\d{1,2})?$/.test(trimmed) ? Number(trimmed) : Number.NaN;
}

/** AntD Tag props for a status group (colour follows the group, P1-2.3). */
export function groupTagProps(group: WorkOrderGroup): {
  color?: string;
  style?: React.CSSProperties;
} {
  switch (group) {
    case 'estimate':
      return { color: 'purple' };
    case 'approved':
      return { color: 'cyan' };
    case 'in-progress':
      return { color: 'blue' };
    case 'waiting':
      return { color: 'magenta' };
    case 'done':
      return { color: 'green' };
    case 'handed-over':
      return { color: 'lime' };
    case 'closed':
      return { color: '#595959' };
    case 'cancelled':
      return { style: { textDecoration: 'line-through' } };
    default:
      return {};
  }
}

/** The name in the UI language: LV names in Latvian, EN names otherwise. */
export function localName(item: { nameLv: string; nameEn: string }, language: string): string {
  return language.startsWith('lv') ? item.nameLv : item.nameEn;
}
